import type { BattleActor, BattleAttack, BattleRound } from '@mezha/api'
import { STATUS_RU } from '../words'

export const PLAYBACK_MS = 2800
export const WANDERER = 'f542874bdb82bd118b3692fc593653f3bac6342571d6a70f8cc056086b945563'
export const LUGAR = '7916c8054c848ea3e9715fd517ebb8b1d599cdd11806a53b894046394fc22a7b'
export type Atlas = 'wanderer' | 'lugar'
export function atlasFor(a: BattleActor): Atlas | null {
  const file = a.portrait.split(/[?#]/)[0].split('/').pop()?.split('.')[0].toLowerCase()
  if (file === LUGAR && a.npc) return 'lugar'
  if (file === WANDERER && a.weapon_family === 'sword' && a.has_shield && !a.statuses.some(s => s.id === 'disarm')) return 'wanderer'
  return null
}

export type SceneEvent = {
  actor: string; target: string; at: number; text: string
  tone: 'hit' | 'crit' | 'guard' | 'magic' | 'heal' | 'status' | 'rest'
  attack?: BattleAttack
  status?: { id: string; result: string; remove?: boolean }
}
const outcomes: Record<string, string> = { miss: 'Промах', dodge: 'Уклонение', parry: 'Парирование' }
const results: Record<string, string> = { resisted: 'сопротивление', absorbed: 'поглощён', immune: 'иммунитет', applied: '' }
const statusName = (id: string) => STATUS_RU[id] ?? id

/** Presentation only: never roll, calculate damage, or derive the winner here. */
export function sceneEvents(round: BattleRound, skillNames: Record<string, string> = {}): SceneEvent[] {
  const events: SceneEvent[] = []
  const hits = new Map<string, number>()
  for (const a of round.attacks) {
    const n = hits.get(a.attacker) ?? 0
    hits.set(a.attacker, n + 1)
    const at = .72 + Math.min(n, 5) * .16
    const parts = [outcomes[a.outcome] ?? (a.applied > 0 ? `−${a.applied}` : 'Без урона')]
    if (a.outcome === 'crit') parts.push('крит!')
    if (a.outcome === 'splash' || a.group_secondary) parts.push('по соседней цели')
    if (a.group_pressure) parts.push('натиск группы')
    if (a.blocked) parts.push('блок')
    else if (a.guarded) parts.push(a.second ? 'вторая защита' : 'защита')
    if (a.absorbed > 0) parts.push(`оберег ${a.absorbed}`)
    events.push({ actor: a.attacker, target: a.target, at, attack: a, text: parts.join(' · '),
      tone: a.outcome === 'crit' ? 'crit' : outcomes[a.outcome] || a.blocked ? 'guard' : a.kind === 'spirit' ? 'magic' : 'hit' })
    for (const s of a.statuses) events.push({ actor: a.attacker, target: a.target, at: at + .24, tone: 'status', status: { id: s.id, result: s.result },
      text: `${statusName(s.id)}${results[s.result] ? `: ${results[s.result]}` : ''}` })
  }
  for (const e of round.effects) {
    const text = e.kind === 'heal' ? `+${e.value} здоровья`
      : e.kind === 'ward' ? 'Оберег'
        : e.kind === 'cleanse' ? e.status ? `Снято: ${statusName(e.status)}` : `Снято эффектов: ${e.value}`
          : e.kind === 'last_stand' ? 'Последний рубеж'
            : `${statusName(e.status) || e.kind}${results[e.result] ? `: ${results[e.result]}` : ''}`
    events.push({ actor: e.actor, target: e.target || e.actor, at: e.kind === 'ward' ? .2 : 1.55,
      ...(e.status ? { status: { id: e.status, result: e.result, remove: e.kind === 'cleanse' } } : {}),
      tone: e.kind === 'heal' || e.kind === 'cleanse' || e.kind === 'last_stand' ? 'heal' : e.kind === 'ward' ? 'magic' : 'status', text })
  }
  for (const a of round.actors) {
    if (a.choice.skill) events.push({ actor: a.id, target: a.id, at: .1, tone: round.attacks.some(hit => hit.attacker === a.id && hit.kind === 'spirit') || round.effects.some(e => e.actor === a.id) ? 'magic' : 'rest', text: skillNames[a.choice.skill] ?? a.choice.skill })
    if (a.choice.action === 'rest') events.push({ actor: a.id, target: a.id, at: .25, tone: 'rest', text: a.choice.auto ? 'Автоход · передышка' : 'Передышка' })
    if (a.choice.action === 'charm') events.push({ actor: a.id, target: a.id, at: .2, tone: 'magic', text: 'Памятный знак' })
    if (a.bleed_tick) events.push({ actor: a.id, target: a.id, at: 1.95, tone: 'status', text: `−${a.bleed_tick} · кровотечение` })
    if (a.out) events.push({ actor: a.id, target: a.id, at: 2.05, tone: 'guard', text: a.out_reason === 'forfeit' ? 'Сдался' : 'Выбыл' })
  }
  return events
}

export function shouldPlay(seen: number, next: BattleRound | undefined, reduced: boolean, hidden: boolean, remaining: number): boolean {
  return Boolean(next && next.round === seen + 1 && !reduced && !hidden && remaining > PLAYBACK_MS + 600)
}
